"use client";

import React, { createContext, useContext, useEffect, useState } from "react";

interface DebugContextValue {
  isDebug: boolean;
  setIsDebug: (value: boolean) => void;
}

const DebugContext = createContext<DebugContextValue | undefined>(undefined);

export const DebugProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isDebug, setIsDebug] = useState<boolean>(false);

  useEffect(() => {
    try {
      const stored = localStorage.getItem("cosimo_debug");
      if (stored === "1") setIsDebug(true);
    } catch {
      // Ignore localStorage errors
    }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem("cosimo_debug", isDebug ? "1" : "0");
    } catch {
      // Ignore localStorage errors
    }
  }, [isDebug]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const isToggleCombo =
        (e.metaKey || e.ctrlKey) && e.shiftKey && (e.key === "D" || e.key === "d");
      if (isToggleCombo) {
        e.preventDefault();
        setIsDebug((prev) => {
          const newValue = !prev;
          console.debug(`[Debug Mode] ${newValue ? "ENABLED" : "DISABLED"}`);
          return newValue;
        });
      }
    };
    window.addEventListener("keydown", handleKeyDown as EventListener);
    return () => window.removeEventListener("keydown", handleKeyDown as EventListener);
  }, []);

  return <DebugContext.Provider value={{ isDebug, setIsDebug }}>{children}</DebugContext.Provider>;
};

export function useDebug(): DebugContextValue {
  const ctx = useContext(DebugContext);
  if (!ctx) throw new Error("useDebug must be used within a DebugProvider");
  return ctx;
}
